/**
 * API Test Page — interactive debugger for Gateway REST APIs, Events & more.
 *
 * Route: /test_api
 * Features:
 *   - Tab 1: REST API tester
 *   - Tab 2: Events API tester (POST /channels/rocky/events)
 *   - Tab 3: File Upload tester
 *   - Tab 4: Skill Marketplace tester
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  FormField,
  Select,
  Input,
  Button,
  Textarea,
  ColumnLayout,
  Box,
  StatusIndicator,
  SelectProps,
  Tabs,
  Badge,
  ExpandableSection,
} from '@amzn/awsui-components-react';
import { appConfig } from '../config';
import { gatewayAuthHeaders } from '../context/gatewayAuthHeaders';
import {
  gatewayClient,
  UploadedFile,
} from '../client/GatewayClient';
import { useAuth } from '../context/AuthContext';
import SkillMarketplaceTester from './SkillMarketplaceTester';

// ── REST API Presets ────────────────────────────────────────────────────────

interface ApiPreset {
  label: string;
  method: string;
  path: string;
  params: string;
  body: string;
}

const API_PRESETS: ApiPreset[] = [
  {
    label: 'Workspace — List root',
    method: 'GET',
    path: '/channels/portal/workspace',
    params: '{"agent": "rocky"}',
    body: '',
  },
  {
    label: 'Workspace — List subfolder',
    method: 'GET',
    path: '/channels/portal/workspace',
    params: '{"agent": "rocky", "prefix": "test/"}',
    body: '',
  },
  {
    label: 'Workspace — Recursive (all files)',
    method: 'GET',
    path: '/channels/portal/workspace',
    params: '{"agent": "rocky", "recursive": "true"}',
    body: '',
  },
  {
    label: 'Skills — List all',
    method: 'GET',
    path: '/channels/portal/skills',
    params: '{}',
    body: '',
  },
  {
    label: 'Skills — Search by keyword',
    method: 'GET',
    path: '/channels/portal/skills',
    params: '{"q": "data"}',
    body: '',
  },
  {
    label: 'File — Get content',
    method: 'GET',
    path: '/channels/portal/workspace/file',
    params: '{"agent": "rocky", "path": "output/test.html"}',
    body: '',
  },
  {
    label: 'File — Get presigned URL',
    method: 'GET',
    path: '/channels/portal/workspace/file',
    params: '{"agent": "rocky", "path": "output/test.html", "mode": "url"}',
    body: '',
  },
  {
    label: 'Health Check',
    method: 'GET',
    path: '/health',
    params: '{}',
    body: '',
  },
  {
    label: 'Custom Request',
    method: 'GET',
    path: '/',
    params: '{}',
    body: '',
  },
];

// ── Helper: build URL preview string ────────────────────────────────────────

function buildUrlPreview(method: string, path: string, params: string): string {
  try {
    const url = new URL(`${appConfig.gatewayUrl}${path}`);
    if (params.trim() && params.trim() !== '{}') {
      const obj = JSON.parse(params) as Record<string, string>;
      Object.entries(obj).forEach(([k, v]) => {
        if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
      });
    }
    return `${method} ${url.toString()}`;
  } catch {
    return `${method} ${appConfig.gatewayUrl}${path} ?(invalid)`;
  }
}

// ── REST API Tester Component ───────────────────────────────────────────────

function RestApiTester() {
  const [selectedPreset, setSelectedPreset] = useState<SelectProps.Option>({
    label: API_PRESETS[0].label,
    value: '0',
  });
  const [method, setMethod] = useState(API_PRESETS[0].method);
  const [path, setPath] = useState(API_PRESETS[0].path);
  const [params, setParams] = useState(API_PRESETS[0].params);
  const [body, setBody] = useState(API_PRESETS[0].body);

  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState<number | null>(null);
  const [responseBody, setResponseBody] = useState<string>('');
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [responseHeaders, setResponseHeaders] = useState<[string, string][]>([]);

  const handlePresetChange = useCallback(
    (detail: { selectedOption: SelectProps.Option }) => {
      const opt = detail.selectedOption;
      setSelectedPreset(opt);
      const idx = parseInt(opt.value ?? '0', 10);
      const preset = API_PRESETS[idx];
      if (preset) {
        setMethod(preset.method);
        setPath(preset.path);
        setParams(preset.params);
        setBody(preset.body);
      }
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setResponseStatus(null);
    setResponseBody('');
    setResponseTime(null);
    setResponseHeaders([]);

    try {
      const url = new URL(`${appConfig.gatewayUrl}${path}`);
      if (params.trim()) {
        try {
          const paramObj = JSON.parse(params) as Record<string, string>;
          Object.entries(paramObj).forEach(([k, v]) => {
            if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
          });
        } catch (e) {
          setResponseBody(`Invalid JSON in query params:\n${String(e)}`);
          setLoading(false);
          return;
        }
      }

      const headers = gatewayAuthHeaders();
      if (body.trim() && method !== 'GET') headers['Content-Type'] = 'application/json';

      const t0 = performance.now();
      const response = await fetch(url.toString(), {
        method,
        headers,
        body: method !== 'GET' && body.trim() ? body : undefined,
      });
      const t1 = performance.now();

      setResponseStatus(response.status);
      setResponseTime(Math.round(t1 - t0));

      const respHeaders: [string, string][] = [];
      response.headers.forEach((v, k) => respHeaders.push([k, v]));
      setResponseHeaders(respHeaders);

      const rawText = await response.text();
      try {
        const json = JSON.parse(rawText);
        setResponseBody(JSON.stringify(json, null, 2));
      } catch {
        setResponseBody(rawText);
      }
    } catch (err) {
      setResponseBody(`Request failed:\n${String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [method, path, params, body]);

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Request</Header>}>
        <SpaceBetween size="m">
          <FormField label="Preset">
            <Select
              selectedOption={selectedPreset}
              onChange={({ detail }) => handlePresetChange(detail)}
              options={API_PRESETS.map((p, i) => ({
                label: p.label,
                value: String(i),
                description: `${p.method} ${p.path}`,
              }))}
            />
          </FormField>

          <ColumnLayout columns={2}>
            <FormField label="Method">
              <Select
                selectedOption={{ label: method, value: method }}
                onChange={({ detail }) => setMethod(detail.selectedOption.value ?? 'GET')}
                options={['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((m) => ({
                  label: m,
                  value: m,
                }))}
              />
            </FormField>
            <FormField label="Path">
              <Input
                value={path}
                onChange={({ detail }) => setPath(detail.value)}
                placeholder="/channels/portal/workspace"
              />
            </FormField>
          </ColumnLayout>

          <FormField label="Query Parameters (JSON)">
            <Textarea
              value={params}
              onChange={({ detail }) => setParams(detail.value)}
              rows={2}
            />
          </FormField>

          {method !== 'GET' && (
            <FormField label="Request Body (JSON)">
              <Textarea
                value={body}
                onChange={({ detail }) => setBody(detail.value)}
                rows={4}
              />
            </FormField>
          )}

          <Box variant="code" fontSize="body-s" color="text-status-info">
            {buildUrlPreview(method, path, params)}
          </Box>

          <Box float="right">
            <Button variant="primary" loading={loading} onClick={handleSubmit}>
              Send Request
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      {(responseStatus !== null || responseBody) && (
        <Container
          header={
            <Header variant="h2">
              Response{' '}
              {responseStatus !== null && (
                <StatusIndicator type={responseStatus < 400 ? 'success' : 'error'}>
                  {responseStatus}
                </StatusIndicator>
              )}
              {responseTime !== null && (
                <Box display="inline-block" margin={{ left: 's' }} fontSize="body-s" color="text-body-secondary">
                  {responseTime}ms
                </Box>
              )}
            </Header>
          }
        >
          <Tabs
            tabs={[
              {
                id: 'body',
                label: 'Body',
                content: (
                  <pre
                    style={{
                      background: '#0d1117',
                      color: '#c9d1d9',
                      padding: '16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      overflow: 'auto',
                      maxHeight: '500px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {responseBody || '(empty)'}
                  </pre>
                ),
              },
              {
                id: 'headers',
                label: `Headers (${responseHeaders.length})`,
                content: (
                  <Box padding="s">
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e0e0e0', textAlign: 'left' }}>
                          <th style={{ padding: '6px 8px', width: '30%' }}>Header</th>
                          <th style={{ padding: '6px 8px' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {responseHeaders.map(([k, v], i) => (
                          <tr key={`${i}-${k}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '4px 8px', fontWeight: 600, fontFamily: 'monospace' }}>{k}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                ),
              },
            ]}
          />
        </Container>
      )}
    </SpaceBetween>
  );
}

// ── Events Tester Component ─────────────────────────────────────────────────

// ── Events Tester Component (SSE Stream) ────────────────────────────────────

/** Anthropic-style SSE event types from Rocky channel */
type RockyEventType =
  | 'message_start'
  | 'text_delta'
  | 'tool_start'
  | 'tool_done'
  | 'file_output'
  | 'interrupt'
  | 'error'
  | 'message_done';

interface RockySSEEvent {
  type: RockyEventType;
  data: Record<string, unknown>;
  raw: string;
  timestamp: string;
}

function getEventColor(type: string): string {
  switch (type) {
    case 'message_start': return '#e8f5e9';
    case 'text_delta': return '#ffffff';
    case 'tool_start': return '#e3f2fd';
    case 'tool_done': return '#e3f2fd';
    case 'file_output': return '#fff3e0';
    case 'interrupt': return '#fff9c4';
    case 'error': return '#fdecea';
    case 'message_done': return '#e8f5e9';
    default: return '#f5f5f5';
  }
}

function getEventBadgeColor(type: string): 'blue' | 'green' | 'grey' | 'red' {
  switch (type) {
    case 'message_start':
    case 'message_done': return 'green';
    case 'text_delta': return 'grey';
    case 'tool_start':
    case 'tool_done': return 'blue';
    case 'error': return 'red';
    case 'interrupt': return 'red';
    default: return 'grey';
  }
}

function EventsTester() {
  const { uid } = useAuth();
  const [agent, setAgent] = useState('rocky');
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [prompt, setPrompt] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [events, setEvents] = useState<RockySSEEvent[]>([]);
  const [accumulatedText, setAccumulatedText] = useState('');
  const [rawStream, setRawStream] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const accTextRef = useRef('');
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  const scrollToBottom = useCallback(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSend = useCallback(async () => {
    if (!prompt.trim() || streaming) return;

    setEvents([]);
    setAccumulatedText('');
    accTextRef.current = '';
    setRawStream('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const body = {
      sessionId,
      agent,
      messages: [{ role: 'user', content: prompt }],
      context: { login: uid || 'anonymous' },
    };

    setPrompt('');

    try {
      const url = `${appConfig.gatewayUrl}/channels/rocky/events`;
      const headers = gatewayAuthHeaders({
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      });

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`;
        try { const e = await response.json(); errMsg = e?.error?.message ?? errMsg; } catch { /* */ }
        setEvents(prev => [...prev, {
          type: 'error',
          data: { message: errMsg, status: response.status },
          raw: errMsg,
          timestamp: new Date().toISOString(),
        }]);
        setStreaming(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const decoded = decoder.decode(value, { stream: true });
        setRawStream(prev => prev + decoded);
        buffer += decoded;

        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() ?? '';

        for (const chunk of chunks) {
          if (!chunk.trim()) continue;

          // Parse SSE: extract event type and data lines
          let eventType = '';
          let dataStr = '';
          for (const line of chunk.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr += (dataStr ? '\n' : '') + line.slice(6);
          }
          // Fallback: if no "event:" field, try parsing data directly
          if (!eventType && dataStr) {
            try {
              const parsed = JSON.parse(dataStr);
              eventType = parsed.type ?? 'unknown';
            } catch { eventType = 'unknown'; }
          }

          let data: Record<string, unknown> = {};
          try { data = JSON.parse(dataStr); } catch { data = { raw: dataStr }; }

          const sseEvent: RockySSEEvent = {
            type: (eventType || data.type || 'unknown') as RockyEventType,
            data,
            raw: dataStr,
            timestamp: new Date().toISOString(),
          };

          setEvents(prev => [...prev, sseEvent]);

          // Accumulate text_delta
          if (sseEvent.type === 'text_delta' && typeof data.delta === 'string') {
            accTextRef.current += data.delta;
            setAccumulatedText(accTextRef.current);
          }
          // message_done may contain full text
          if (sseEvent.type === 'message_done' && typeof data.text === 'string' && !accTextRef.current) {
            accTextRef.current = data.text;
            setAccumulatedText(data.text);
          }

          setTimeout(scrollToBottom, 50);
        }
      }

      // Flush remaining
      buffer += decoder.decode();
      if (buffer.trim()) {
        let dataStr = '';
        for (const line of buffer.split('\n')) {
          if (line.startsWith('data: ')) dataStr += (dataStr ? '\n' : '') + line.slice(6);
        }
        if (dataStr) {
          try {
            const data = JSON.parse(dataStr);
            setEvents(prev => [...prev, {
              type: data.type ?? 'unknown',
              data,
              raw: dataStr,
              timestamp: new Date().toISOString(),
            }]);
          } catch { /* ignore */ }
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setEvents(prev => [...prev, {
          type: 'error',
          data: { message: String(err) },
          raw: String(err),
          timestamp: new Date().toISOString(),
        }]);
      }
    } finally {
      if (!controller.signal.aborted) setStreaming(false);
      abortRef.current = null;
    }
  }, [prompt, streaming, sessionId, agent, uid, scrollToBottom]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, []);

  const handleNewSession = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    accTextRef.current = '';
    setStreaming(false);
    setSessionId(crypto.randomUUID());
    setEvents([]);
    setAccumulatedText('');
    setRawStream('');
  }, []);

  return (
    <SpaceBetween size="l">
      {/* Config */}
      <Container header={<Header variant="h2">Rocky Events Configuration</Header>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Agent Name">
              <Input value={agent} onChange={({ detail }) => setAgent(detail.value)} placeholder="rocky" />
            </FormField>
            <FormField label="Session ID">
              <SpaceBetween size="xs" direction="horizontal">
                <Input value={sessionId} onChange={({ detail }) => setSessionId(detail.value)} readOnly />
                <Button onClick={handleNewSession} iconName="refresh">New</Button>
              </SpaceBetween>
            </FormField>
          </ColumnLayout>

          <Box variant="code" fontSize="body-s" color="text-status-info">
            POST {appConfig.gatewayUrl}/channels/rocky/events
          </Box>

          <Box fontSize="body-s" color="text-body-secondary">
            SSE events: message_start → text_delta → tool_start → tool_done → file_output → interrupt → error → message_done
          </Box>
        </SpaceBetween>
      </Container>

      {/* SSE Events */}
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${events.length})`}
            actions={
              <Button onClick={() => { setEvents([]); setAccumulatedText(''); setRawStream(''); accTextRef.current = ''; }} iconName="remove" disabled={streaming}>
                Clear
              </Button>
            }
          >
            SSE Events
          </Header>
        }
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '8px' }}>
          {events.length === 0 ? (
            <Box textAlign="center" color="text-body-secondary" padding="l">
              No events yet. Send a prompt to start streaming.
            </Box>
          ) : (
            <SpaceBetween size="xs">
              {events.map((evt, i) => (
                <div
                  key={`${i}-${evt.type}`}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: getEventColor(evt.type),
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <Badge color={getEventBadgeColor(evt.type)}>{evt.type}</Badge>
                    <Box fontSize="body-s" color="text-body-secondary">
                      {new Date(evt.timestamp).toLocaleTimeString()}
                    </Box>
                  </div>

                  {evt.type === 'text_delta' && (
                    <Box fontSize="body-s"><code>{String(evt.data.delta ?? '')}</code></Box>
                  )}
                  {evt.type === 'message_start' && (
                    <Box fontSize="body-s" color="text-status-success">
                      ✅ Stream started — messageId: {String(evt.data.messageId ?? evt.data.message_id ?? '?')}
                    </Box>
                  )}
                  {evt.type === 'message_done' && (
                    <Box fontSize="body-s" color="text-status-success">
                      ✅ Stream complete {evt.data.text ? `(${String(evt.data.text).length} chars)` : ''}
                    </Box>
                  )}
                  {evt.type === 'tool_start' && (
                    <Box fontSize="body-s">🔧 Tool: <code>{String(evt.data.name ?? '?')}</code></Box>
                  )}
                  {evt.type === 'tool_done' && (
                    <Box fontSize="body-s">
                      🔧 Tool done: <code>{String(evt.data.name ?? '?')}</code> — {String(evt.data.status ?? '?')}
                    </Box>
                  )}
                  {evt.type === 'file_output' && (
                    <Box fontSize="body-s">📄 File: <code>{String(evt.data.path ?? '?')}</code></Box>
                  )}
                  {evt.type === 'interrupt' && (
                    <Box fontSize="body-s" color="text-status-warning">
                      ⚠️ Interrupt: {String(evt.data.name ?? '?')} (tool: {String(evt.data.tool ?? '?')})
                    </Box>
                  )}
                  {evt.type === 'error' && (
                    <Box color="text-status-error">❌ {String(evt.data.message ?? JSON.stringify(evt.data))}</Box>
                  )}

                  <ExpandableSection headerText="Raw JSON" variant="footer">
                    <pre style={{
                      background: '#0d1117', color: '#c9d1d9', padding: '8px',
                      borderRadius: '4px', fontSize: '11px', overflow: 'auto', maxHeight: '200px',
                    }}>
                      {JSON.stringify(evt.data, null, 2)}
                    </pre>
                  </ExpandableSection>
                </div>
              ))}
              <div ref={eventsEndRef} />
            </SpaceBetween>
          )}
        </div>
      </Container>

      {/* Accumulated text */}
      {accumulatedText && (
        <Container header={<Header variant="h2">Accumulated Response</Header>}>
          <pre style={{
            background: '#0d1117', color: '#c9d1d9', padding: '16px', borderRadius: '8px',
            fontSize: '13px', lineHeight: '1.6', overflow: 'auto', maxHeight: '400px',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {accumulatedText}
            {streaming && <span style={{ animation: 'blink 1s infinite' }}>▊</span>}
          </pre>
        </Container>
      )}

      {/* Raw SSE */}
      {rawStream && (
        <Container header={<Header variant="h2">Raw SSE Stream</Header>}>
          <pre style={{
            background: '#1a1a2e', color: '#e94560', padding: '16px', borderRadius: '8px',
            fontSize: '11px', lineHeight: '1.4', overflow: 'auto', maxHeight: '300px',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {rawStream}
          </pre>
        </Container>
      )}

      {/* Input */}
      <Container>
        <SpaceBetween size="s">
          <FormField label="Prompt">
            <Textarea
              value={prompt}
              onChange={({ detail }) => setPrompt(detail.value)}
              placeholder="Type your message here..."
              rows={3}
              disabled={streaming}
              onKeyDown={(e) => {
                if ((e.detail.ctrlKey || e.detail.metaKey) && e.detail.key === 'Enter') handleSend();
              }}
            />
          </FormField>
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              {streaming ? (
                <Button onClick={handleStop} variant="normal">⏹ Stop</Button>
              ) : (
                <Button onClick={handleSend} variant="primary" disabled={!prompt.trim()}>Send Event</Button>
              )}
            </SpaceBetween>
          </Box>
          <Box fontSize="body-s" color="text-body-secondary">
            Ctrl+Enter to send • Session: {sessionId.slice(0, 8)}...
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}


// ── Upload Tester Component ─────────────────────────────────────────────────

function UploadTester() {
  const [sessionId] = useState<string>(() => crypto.randomUUID());
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadTime, setUploadTime] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    // Reset input value so re-selecting the same file triggers onChange (AutoSDE #5)
    e.target.value = '';
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadResult('');
    setUploadedFiles([]);
    setUploadTime(null);

    try {
      const t0 = performance.now();
      const result = await gatewayClient.uploadFiles(selectedFiles, sessionId);
      const t1 = performance.now();
      setUploadTime(Math.round(t1 - t0));
      setUploadResult(JSON.stringify(result, null, 2));
      setUploadedFiles(result.files);
    } catch (err) {
      setUploadResult(`Upload failed:\n${String(err)}`);
    } finally {
      setUploading(false);
    }
  }, [selectedFiles, sessionId]);

  const handleClear = useCallback(() => {
    setSelectedFiles([]);
    setUploadResult('');
    setUploadedFiles([]);
    setUploadTime(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">File Upload Test</Header>}>
        <SpaceBetween size="m">
          <FormField label="Session ID (for S3 key)">
            <Input value={sessionId} readOnly />
          </FormField>

          <FormField label="Select Files" description="Images, PDFs, CSVs, documents, etc.">
            <SpaceBetween size="xs">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                accept="image/*,.pdf,.csv,.txt,.md,.html,.doc,.docx,.xls,.xlsx,.json,.xml,.yaml,.yml,.mp4,.webm"
                style={{ marginBottom: '8px' }}
              />
              {selectedFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedFiles.map((file, i) => (
                    <span
                      key={`${file.name}-${i}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: '#f0f4f8',
                        border: '1px solid #d0d7de',
                        borderRadius: '12px',
                        fontSize: '13px',
                      }}
                    >
                      {file.type.startsWith('image/') ? '🖼️' : '📎'}
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      <button
                        onClick={() => handleRemoveFile(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cf222e', fontSize: '14px' }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </SpaceBetween>
          </FormField>

          <Box variant="code" fontSize="body-s" color="text-status-info">
            POST {appConfig.gatewayUrl}/channels/portal/upload (multipart/form-data)
          </Box>

          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button onClick={handleClear} disabled={uploading}>Clear</Button>
              <Button
                variant="primary"
                loading={uploading}
                onClick={handleUpload}
                disabled={selectedFiles.length === 0}
              >
                Upload {selectedFiles.length > 0 ? `(${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''})` : ''}
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Container>

      {uploadResult && (
        <Container
          header={
            <Header variant="h2">
              Upload Result{' '}
              {uploadedFiles.length > 0 && (
                <StatusIndicator type="success">
                  {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} uploaded
                </StatusIndicator>
              )}
              {uploadTime !== null && (
                <Box display="inline-block" margin={{ left: 's' }} fontSize="body-s" color="text-body-secondary">
                  {uploadTime}ms
                </Box>
              )}
            </Header>
          }
        >
          <SpaceBetween size="m">
            {/* File cards */}
            {uploadedFiles.map((f, i) => (
              <div
                key={f.file_id || i}
                style={{
                  padding: '12px',
                  background: '#f6f8fa',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                }}
              >
                <SpaceBetween size="xxs">
                  <Box fontWeight="bold">
                    {f.type === 'image' ? '🖼️' : f.type === 'video' ? '🎬' : '📄'} {f.filename}
                  </Box>
                  <Box fontSize="body-s" color="text-body-secondary">
                    Type: {f.type}/{f.format} • Size: {(f.size / 1024).toFixed(1)} KB • MIME: {f.mimetype}
                  </Box>
                  <Box variant="code" fontSize="body-s">{f.s3_uri}</Box>
                </SpaceBetween>
              </div>
            ))}

            {/* Raw JSON */}
            <ExpandableSection headerText="Raw JSON Response">
              <pre
                style={{
                  background: '#0d1117',
                  color: '#c9d1d9',
                  padding: '16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  overflow: 'auto',
                  maxHeight: '400px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {uploadResult}
              </pre>
            </ExpandableSection>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
}

// ── Events Tester Component ─────────────────────────────────────────────────

function EventsApiTester() {
  const [eventType, setEventType] = useState('test_event');
  const [eventPayload, setEventPayload] = useState('{}');
  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState<number | null>(null);
  const [responseBody, setResponseBody] = useState('');
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [responseHeaders, setResponseHeaders] = useState<[string, string][]>([]);

  const EVENT_TYPE_OPTIONS = [
    { label: 'test_event', value: 'test_event' },
    { label: 'task_completed', value: 'task_completed' },
    { label: 'task_failed', value: 'task_failed' },
    { label: 'notification', value: 'notification' },
    { label: 'custom', value: 'custom' },
  ];

  const handleSend = useCallback(async () => {
    setLoading(true);
    setResponseStatus(null);
    setResponseBody('');
    setResponseTime(null);
    setResponseHeaders([]);

    try {
      let parsedPayload: unknown = {};
      if (eventPayload.trim()) {
        try {
          parsedPayload = JSON.parse(eventPayload);
        } catch (e) {
          setResponseBody(`Invalid JSON in payload:\n${String(e)}`);
          setLoading(false);
          return;
        }
      }

      const requestBody = JSON.stringify({
        eventType,
        payload: parsedPayload,
      });

      const headers = gatewayAuthHeaders({ 'Content-Type': 'application/json' });

      const t0 = performance.now();
      const response = await fetch(`${appConfig.gatewayUrl}/channels/rocky/events`, {
        method: 'POST',
        headers,
        body: requestBody,
      });
      const t1 = performance.now();

      setResponseStatus(response.status);
      setResponseTime(Math.round(t1 - t0));

      const respHeaders: [string, string][] = [];
      response.headers.forEach((v, k) => respHeaders.push([k, v]));
      setResponseHeaders(respHeaders);

      const rawText = await response.text();
      try {
        const json = JSON.parse(rawText);
        setResponseBody(JSON.stringify(json, null, 2));
      } catch {
        setResponseBody(rawText);
      }
    } catch (err) {
      setResponseBody(`Request failed:\n${String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [eventType, eventPayload]);

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Post Event</Header>}>
        <SpaceBetween size="m">
          <FormField label="Event Type">
            <Select
              selectedOption={{ label: eventType, value: eventType }}
              onChange={({ detail }) => setEventType(detail.selectedOption.value ?? 'test_event')}
              options={EVENT_TYPE_OPTIONS}
            />
          </FormField>

          <FormField label="Payload (JSON)">
            <Textarea
              value={eventPayload}
              onChange={({ detail }) => setEventPayload(detail.value)}
              rows={6}
              placeholder='{"key": "value"}'
            />
          </FormField>

          <Box variant="code" fontSize="body-s" color="text-status-info">
            POST {appConfig.gatewayUrl}/channels/rocky/events
          </Box>

          <ExpandableSection headerText="Request Preview">
            <pre
              style={{
                background: '#0d1117',
                color: '#c9d1d9',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '13px',
                overflow: 'auto',
                maxHeight: '200px',
              }}
            >
              {JSON.stringify({ eventType, payload: (() => { try { return JSON.parse(eventPayload); } catch { return eventPayload; } })() }, null, 2)}
            </pre>
          </ExpandableSection>

          <Box float="right">
            <Button variant="primary" loading={loading} onClick={handleSend}>
              Send Event
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      {(responseStatus !== null || responseBody) && (
        <Container
          header={
            <Header variant="h2">
              Response{' '}
              {responseStatus !== null && (
                <StatusIndicator type={responseStatus < 400 ? 'success' : 'error'}>
                  {responseStatus}
                </StatusIndicator>
              )}
              {responseTime !== null && (
                <Box display="inline-block" margin={{ left: 's' }} fontSize="body-s" color="text-body-secondary">
                  {responseTime}ms
                </Box>
              )}
            </Header>
          }
        >
          <Tabs
            tabs={[
              {
                id: 'body',
                label: 'Body',
                content: (
                  <pre
                    style={{
                      background: '#0d1117',
                      color: '#c9d1d9',
                      padding: '16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      overflow: 'auto',
                      maxHeight: '500px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {responseBody || '(empty)'}
                  </pre>
                ),
              },
              {
                id: 'headers',
                label: `Headers (${responseHeaders.length})`,
                content: (
                  <Box padding="s">
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e0e0e0', textAlign: 'left' }}>
                          <th style={{ padding: '6px 8px', width: '30%' }}>Header</th>
                          <th style={{ padding: '6px 8px' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {responseHeaders.map(([k, v], i) => (
                          <tr key={`${i}-${k}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '4px 8px', fontWeight: 600, fontFamily: 'monospace' }}>{k}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                ),
              },
            ]}
          />
        </Container>
      )}
    </SpaceBetween>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function ApiTestPage() {
  return (
    <ContentLayout
      header={
        <Header variant="h1" description="Interactive debugger for Gateway REST APIs and Chat">
          API Test Console
        </Header>
      }
    >
      <Tabs
        tabs={[
          {
            id: 'rest',
            label: '🔌 REST API',
            content: <RestApiTester />,
          },
          {
            id: 'events',
            label: '📡 Events',
            content: <EventsTester />,
          },
          {
            id: 'upload',
            label: '📎 File Upload',
            content: <UploadTester />,
          },
          {
            id: 'events-api',
            label: '📡 Events API',
            content: <EventsApiTester />,
          },
          {
            id: 'skills',
            label: '🎯 Skill Marketplace',
            content: <SkillMarketplaceTester />,
          },
        ]}
      />
    </ContentLayout>
  );
}
